import { useEffect, useRef, useState } from "react";
import { MessageCircle, Send, Sparkles, X, Headphones, ArrowLeft, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { toast } from "sonner";
import { sendChatMessage, submitSupportTicket } from "@/lib/afritask";

/**
 * Floating support chat bubble + panel. Powered by Gemini via /api/chat.
 * Mounts only inside AppShell (i.e. /app/*) — never on landing or auth pages.
 *
 * Props:
 *   me — the current user object (used to pre-fill live-agent form)
 */
export default function SupportChat({ me }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("chat"); // "chat" | "ticket" | "ticket-sent"
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi 👋 I'm AfriTask Assistant. Ask me anything about how the platform works, payments, tasks, or referrals." },
  ]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [sending, setSending] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages, view]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setSending(true);
    try {
      const r = await sendChatMessage(text, sessionId);
      setSessionId(r.session_id);
      setMessages((m) => [...m, { role: "assistant", text: r.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: "Sorry — I'm having trouble reaching my brain right now. Please connect to a live agent below." }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating bubble */}
      <button
        type="button"
        onClick={() => { setOpen(true); setView("chat"); }}
        data-testid="support-chat-bubble"
        aria-label="Open support chat"
        className="fixed bottom-20 right-4 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-amber-700 text-white shadow-lg shadow-amber-600/40 transition hover:scale-105 hover:bg-amber-800 active:scale-95 md:bottom-6 md:right-6"
      >
        <span className="absolute inset-0 -z-0 animate-ping rounded-full bg-amber-600/40" style={{ animationDuration: "2.4s" }} />
        <MessageCircle className="h-6 w-6" strokeWidth={2.25} />
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="flex w-full max-w-md flex-col p-0 md:w-[420px]"
          data-testid="support-chat-panel"
        >
          {view === "ticket-sent" ? (
            <TicketSent onDone={() => setOpen(false)} />
          ) : view === "ticket" ? (
            <LiveAgentForm
              me={me}
              onBack={() => setView("chat")}
              onDone={() => setView("ticket-sent")}
            />
          ) : (
            <ChatView
              messages={messages}
              input={input}
              setInput={setInput}
              onSend={send}
              sending={sending}
              scrollerRef={scrollerRef}
              onLiveAgent={() => setView("ticket")}
              onClose={() => setOpen(false)}
            />
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}

// ─────────────────────────── ChatView ───────────────────────────
function ChatView({ messages, input, setInput, onSend, sending, scrollerRef, onLiveAgent, onClose }) {
  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };
  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center justify-between border-b border-neutral-200 bg-gradient-to-br from-amber-50 to-white px-4 py-3">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-700 text-white">
            <Sparkles className="h-4 w-4" />
          </span>
          <div>
            <p className="text-sm font-semibold text-neutral-900">AfriTask Assistant</p>
            <p className="text-[10px] font-medium text-neutral-500">Powered by Gemini</p>
          </div>
        </div>
        <button onClick={onClose} className="rounded-md p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700" aria-label="Close">
          <X className="h-4 w-4" />
        </button>
      </header>

      <div ref={scrollerRef} className="flex-1 overflow-y-auto bg-gradient-to-b from-white to-neutral-50 px-4 py-4" data-testid="support-chat-thread">
        <div className="space-y-3">
          {messages.map((m, i) => (
            <Bubble key={i} role={m.role} text={m.text} />
          ))}
          {sending && <Bubble role="assistant" text="…" typing />}
        </div>
      </div>

      <div className="border-t border-neutral-200 bg-white p-3">
        <div className="flex items-end gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Ask about payments, tasks, referrals…"
            rows={1}
            className="min-h-[42px] resize-none rounded-lg"
            data-testid="support-chat-input"
            disabled={sending}
          />
          <Button
            onClick={onSend}
            disabled={sending || !input.trim()}
            data-testid="support-chat-send"
            className="h-[42px] shrink-0 rounded-lg bg-amber-700 hover:bg-amber-800"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
        <div className="mt-2 flex items-center justify-between text-[10px] text-neutral-500">
          <span>AI-generated responses. For urgent matters,{" "}</span>
          <button
            onClick={onLiveAgent}
            data-testid="support-live-agent-link"
            className="inline-flex items-center gap-1 font-semibold text-amber-700 hover:text-amber-900"
          >
            <Headphones className="h-3 w-3" /> connect to a live agent
          </button>
        </div>
      </div>
    </div>
  );
}

function Bubble({ role, text, typing }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2 text-sm leading-relaxed shadow-sm ${
          isUser
            ? "rounded-br-md bg-amber-700 text-white"
            : "rounded-bl-md border border-neutral-200 bg-white text-neutral-800"
        }`}
      >
        {typing ? (
          <span className="inline-flex items-center gap-0.5">
            <span className="loading-dot" />
            <span className="loading-dot" style={{ animationDelay: "160ms" }} />
            <span className="loading-dot" style={{ animationDelay: "320ms" }} />
          </span>
        ) : (
          text
        )}
      </div>
    </div>
  );
}

// ─────────────────────────── Live agent form ───────────────────────────
function LiveAgentForm({ me, onBack, onDone }) {
  const [name, setName]       = useState(me?.username || "");
  const [phone, setPhone]     = useState(me?.phone   || "");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !phone || !subject || !message) return;
    setSubmitting(true);
    try {
      await submitSupportTicket({ name, phone, subject, message });
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send your message");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex h-full flex-col" data-testid="support-ticket-form">
      <header className="flex items-center gap-2 border-b border-neutral-200 px-3 py-3">
        <button type="button" onClick={onBack} aria-label="Back" className="rounded p-1 text-neutral-500 hover:bg-neutral-100">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div>
          <p className="text-sm font-semibold text-neutral-900">Connect to a live agent</p>
          <p className="text-[10px] font-medium text-neutral-500">We'll respond via SMS or WhatsApp within 24h</p>
        </div>
      </header>

      <div className="flex-1 space-y-3 overflow-y-auto p-4">
        <div>
          <Label className="text-xs text-neutral-700">Your name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)}
                 className="mt-1" data-testid="support-name" placeholder="e.g. Adebayo" />
        </div>
        <div>
          <Label className="text-xs text-neutral-700">Phone</Label>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)}
                 className="mt-1 font-mono" data-testid="support-phone" placeholder="+234…" />
        </div>
        <div>
          <Label className="text-xs text-neutral-700">Subject</Label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                 className="mt-1" data-testid="support-subject" placeholder="Withdrawal issue, account help, etc." />
        </div>
        <div>
          <Label className="text-xs text-neutral-700">Message</Label>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)}
                    rows={5} className="mt-1" data-testid="support-message"
                    placeholder="Tell us what's going on…" />
        </div>
      </div>

      <div className="border-t border-neutral-200 bg-white p-3">
        <Button
          type="submit"
          disabled={submitting || !name || !phone || !subject || !message}
          className="w-full rounded-lg bg-amber-700 hover:bg-amber-800"
          data-testid="support-ticket-submit"
        >
          {submitting ? "Sending…" : "Send to live agent"}
        </Button>
      </div>
    </form>
  );
}

function TicketSent({ onDone }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 p-8 text-center" data-testid="support-ticket-sent">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
        <CheckCircle2 className="h-8 w-8 text-emerald-600" />
      </div>
      <h3 className="font-display text-xl font-semibold text-neutral-900">Message sent</h3>
      <p className="max-w-xs text-sm text-neutral-600">
        We&apos;ll reach out via SMS or WhatsApp within <b>24 hours</b>.
      </p>
      <Button onClick={onDone} className="mt-2 rounded-lg bg-amber-700 hover:bg-amber-800">
        Close
      </Button>
    </div>
  );
}
